/* Shared admin UI primitives — used across every admin page for visual consistency. */
import { useEffect } from "react";
import { Link } from "react-router-dom";

export function PageHead({ title, sub, right }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 22 }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em" }}>{title}</h1>
        {sub ? <div style={{ fontSize: 11, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 6 }}>{sub}</div> : null}
      </div>
      {right}
    </div>
  );
}

export function Card({ title, right, children, pad = 16, style }) {
  return (
    <section style={{
      background: "var(--pl-surface)", border: "1px solid var(--pl-border)", borderRadius: 14,
      padding: pad, ...style,
    }}>
      {(title || right) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          {title ? <div style={{ fontSize: 10, color: "var(--pl-aqua)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>{title}</div> : <span />}
          {right || null}
        </div>
      )}
      {children}
    </section>
  );
}

export function Kpi({ label, value, sub, tone, testId }) {
  const colors = { danger: "#fda4af", warn: "#fcd34d", ok: "#86efac" };
  const fg = (tone && value > 0) ? colors[tone] : "var(--pl-text)";
  return (
    <Card pad={14}>
      <div data-testid={testId} style={{ display: "block" }}>
        <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: fg, marginTop: 4 }}>{value ?? "—"}</div>
        {sub ? <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 2 }}>{sub}</div> : null}
      </div>
    </Card>
  );
}

export function HealthDot({ status }) {
  const map = {
    healthy: "#34d399", "in-process": "#34d399", local: "#34d399",
    warning: "#fcd34d", degraded: "#fcd34d",
    error: "#fda4af", critical: "#fda4af",
    not_configured: "#94a3b8", not_implemented: "#94a3b8",
  };
  const color = map[status] || "#94a3b8";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color, boxShadow: `0 0 12px ${color}` }} />
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--pl-text)" }}>{status.replace(/_/g, " ")}</span>
    </span>
  );
}

export function Pill({ children, tone = "muted" }) {
  const tones = {
    aqua:    ["rgba(39,230,209,0.12)", "var(--pl-aqua)"],
    lime:    ["rgba(185,255,47,0.14)", "#cef76b"],
    muted:   ["rgba(255,255,255,0.06)", "var(--pl-text-muted)"],
    success: ["rgba(34,197,94,0.14)",  "#86efac"],
    danger:  ["rgba(244,63,94,0.14)",  "#fda4af"],
    warn:    ["rgba(252,211,77,0.14)", "#fcd34d"],
  };
  const [bg, fg] = tones[tone] || tones.muted;
  return (
    <span style={{ padding: "3px 9px", borderRadius: 999, background: bg, color: fg,
      fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

export function StatusPill({ s }) {
  const map = {
    succeeded: "success", active: "success", healthy: "success", queued: "aqua",
    running: "aqua", claimed: "aqua", retrying: "warn",
    failed: "danger", critical: "danger", error: "danger", suspended: "danger", past_due: "danger",
    canceled: "warn", trialing: "warn", warning: "warn",
    not_configured: "muted", info: "muted",
  };
  return <Pill tone={map[s] || "muted"}>{s}</Pill>;
}

export function FilterBar({ children }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14, alignItems: "center" }}>{children}</div>
  );
}

export function TextInput({ value, onChange, placeholder, ...rest }) {
  return (
    <input value={value} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder}
      style={{ height: 32, padding: "0 10px", borderRadius: 9, background: "var(--pl-bg-2)",
        border: "1px solid var(--pl-border)", color: "var(--pl-text)", fontSize: 12,
        minWidth: 180, outline: "none" }} {...rest} />
  );
}

export function Select({ value, onChange, options = [], placeholder, ...rest }) {
  return (
    <select value={value} onChange={(e) => onChange?.(e.target.value)}
      style={{ height: 32, padding: "0 10px", borderRadius: 9, background: "var(--pl-bg-2)",
        border: "1px solid var(--pl-border)", color: "var(--pl-text)", fontSize: 12, outline: "none" }} {...rest}>
      <option value="">{placeholder || "All"}</option>
      {options.map((o) => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function Btn({ children, onClick, tone = "default", testId, type = "button", disabled, style }) {
  const tones = {
    default: { color: "var(--pl-text)", background: "var(--pl-surface)", border: "1px solid var(--pl-border)" },
    primary: { color: "#001b18",      background: "linear-gradient(135deg, var(--pl-aqua), #B9FF2F)", border: 0 },
    danger:  { color: "#fda4af",       background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.32)" },
    ghost:   { color: "var(--pl-aqua)", background: "rgba(39,230,209,0.10)", border: "1px solid rgba(39,230,209,0.32)" },
  };
  return (
    <button data-testid={testId} type={type} disabled={disabled} onClick={onClick}
      style={{ height: 32, padding: "0 12px", borderRadius: 9, fontWeight: 800, fontSize: 12, cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 6, opacity: disabled ? 0.5 : 1, ...tones[tone], ...(style || {}) }}>
      {children}
    </button>
  );
}

export function EmptyState({ title, description, action }) {
  return (
    <div style={{ padding: "40px 18px", textAlign: "center", borderRadius: 14, border: "1px dashed var(--pl-border)", color: "var(--pl-text-muted)" }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "var(--pl-text)", marginBottom: 6 }}>{title}</div>
      {description ? <div style={{ fontSize: 12, marginBottom: 12, maxWidth: 480, marginInline: "auto" }}>{description}</div> : null}
      {action || null}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, width = 540 }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    if (open) document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.48)" }} />
      <aside style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: `min(${width}px, calc(100vw - 32px))`,
        background: "var(--pl-surface)", borderLeft: "1px solid var(--pl-border)",
        boxShadow: "-24px 0 70px rgba(0,0,0,0.42)", zIndex: 61, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--pl-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <button onClick={onClose} style={{ background: "transparent", border: 0, color: "var(--pl-text-muted)", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <div style={{ overflow: "auto", padding: 20, flex: 1 }}>{children}</div>
      </aside>
    </>
  );
}

export function Loading() {
  return <div style={{ color: "var(--pl-text-muted)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" }}>Loading…</div>;
}
export function Err({ msg }) {
  return <div style={{ color: "#fda4af", padding: 12, borderRadius: 10, background: "rgba(244,63,94,0.10)", border: "1px solid rgba(244,63,94,0.32)" }}>{msg}</div>;
}

export function AdminControlCard({ icon, title, description, status, statusTone = "muted", to, action = "Open", testId }) {
  return (
    <Link to={to} data-testid={testId} style={{
      display: "block", textDecoration: "none", padding: 16, borderRadius: 14,
      background: "var(--pl-surface)", border: "1px solid var(--pl-border)",
      color: "var(--pl-text)", transition: "border-color 140ms, transform 140ms",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(39,230,209,0.42)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--pl-border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <span style={{
          width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center",
          background: "rgba(39,230,209,0.12)", color: "var(--pl-aqua)", flexShrink: 0,
        }}>{icon}</span>
        {status ? <Pill tone={statusTone}>{status}</Pill> : null}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, marginTop: 12 }}>{title}</div>
      <div style={{ fontSize: 11.5, color: "var(--pl-text-muted)", marginTop: 4, lineHeight: 1.5, minHeight: 32 }}>{description}</div>
      <div style={{ marginTop: 12, fontSize: 11, color: "var(--pl-aqua)", fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
        {action} →
      </div>
    </Link>
  );
}

export function SectionTitle({ children, sub }) {
  return (
    <div style={{ margin: "26px 0 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--pl-aqua)" }}>{children}</div>
      {sub ? <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}

export const tableStyles = {
  table: { width: "100%", fontSize: 12, borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800, background: "var(--pl-bg-2)" },
  td: { padding: "10px 12px", verticalAlign: "top", borderTop: "1px solid var(--pl-border)" },
};
